import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Country } from "country-state-city";

import * as Yup from "yup";
import { Link } from "react-router-dom";
import { post } from "../../helpers/api_helper";
import { toast } from "react-toastify";
import ThankyouScreen from "../../components/common/ThankyouScreen";
import BookingSection from "../../components/common/BookingSection";

const BookDemoSchema = Yup.object().shape({
  fullName: Yup.string()
    .trim()
    .required("Full Name is required")
    .min(2, "Full Name must be at least 2 characters"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Business Email is required"),
  industry: Yup.string().required("Industry is required"),
  company: Yup.string()
    .trim()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters"),
  companySize: Yup.string().required("Company Size is required"),
  country: Yup.string().required("Country is required"),
  subject: Yup.string().required("Subject is required"),
  body: Yup.string()
    .max(240, "Message cannot exceed 240 characters")
    .optional(),
});

const BookDemo = () => {
  const [showBookingSection, setShowBookingSection] = useState(false);
  const [userFormData, setUserFormData] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const industries = [
    { value: "fintech", label: "Fintech" },
    { value: "banking", label: "Banking" },
    { value: "insurance", label: "Insurance" },
    { value: "legal", label: "Legal" },
    { value: "compliance", label: "Compliance" },
    { value: "other", label: "Other" },
  ];

  const companySizes = [
    { value: "1-10", label: "1-10 Employees" },
    { value: "11-50", label: "11-50 Employees" },
    { value: "51-200", label: "51-200 Employees" },
    { value: "201-500", label: "201-500 Employees" },
    { value: "500+", label: "500+ Employees" },
  ];

  const countries = Country.getAllCountries().map((country) => ({
    value: country.name,
    label: country.name,
  }));
  return (
    <>
      {bookingComplete ? (
        <ThankyouScreen
          title="Thank You for Booking a Demo"
          message="We’re excited to show you how RegTechFlow can make a difference for your business.
Your demo has been successfully scheduled, and you should have received a calendar invitation with all the details."
          message2="We look forward to speaking with you at the scheduled time!"
        />
      ) : showBookingSection ? (
        <BookingSection
          userFormData={userFormData}
          onBookingComplete={() => {
            setBookingComplete(true);
          }}
        />
      ) : (
        <div className="font-sans flex justify-center p-4 lg:p-10">
          <div className="max-w-1200 w-full flex flex-col lg:flex-row justify-between overflow-hidden px-2">
            <div className="py-8">
              <div className="w-full lg:max-w-[446px]">
                <h2 className="text-xl sm:text-[32px] font-bold text-darkPurple mb-3">
                  Book a Demo
                </h2>
                <p className="text-lg text-darkGray/80 mb-6 sm:mb-8">
                  Let RegTechFlow handle the boring stuff! See how we automate
                  corporate, compliance and licensing processes in just a few
                  clicks.
                </p>
              </div>
              <div className="mb-6 sm:mb-8">
                <p className="text-lg text-darkGray/70 mb-2">
                  Ready to see it in action?
                </p>
                <h3 className="text-[#404653] text-lg font-bold mb-2">
                  Book your demo today!
                </h3>
              </div>
            </div>

            <div className="bg-white shadow-[0px_8px_24px_-3px_#1018281A,0px_8px_24px_-3px_#1018280D] rounded-3xl p-6 lg:w-[693px] my-5">
              <Formik
                initialValues={{
                  fullName: "",
                  email: "",
                  industry: "",
                  company: "",
                  companySize: "",
                  country: "",
                  subject: "",
                  body: "",
                }}
                validationSchema={BookDemoSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  try {
                    const params = new URLSearchParams(values).toString();
                    await post(`/Email/SendRequestDemoEmail?${params}`);
                    resetForm();
                    toast.success("Form submitted successfully!");
                    setSubmitting(false);

                    setUserFormData(values);
                    setShowBookingSection(true);
                  } catch (error) {
                    console.log(error, "error while form submitting");
                    setSubmitting(false);
                  }
                }}
              >
                {({ values, errors, touched, isSubmitting }) => (
                  <Form className="flex flex-col gap-8">
                    <div>
                      <label
                        htmlFor="fullName"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        Full Name
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <Field
                        type="text"
                        id="fullName"
                        name="fullName"
                        placeholder="Full Name"
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 text-sm ${
                          touched.fullName && errors.fullName
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-200 focus:ring-primary"
                        }`}
                      />
                      <ErrorMessage
                        name="fullName"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        Business Email
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Work Email"
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 text-sm ${
                          touched.email && errors.email
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-200 focus:ring-primary"
                        }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="industry"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        Industry
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <div className="relative">
                        <Field
                          as="select"
                          id="industry"
                          name="industry"
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md appearance-none focus:outline-none focus:ring-1 text-[#9CA3AF] bg-white text-sm ${
                            touched.industry && errors.industry
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-200 focus:ring-primary"
                          }`}
                        >
                          <option value="" disabled className="text-[#9CA3AF]">
                            Select Industry
                          </option>
                          {industries.map((industry) => (
                            <option
                              key={industry.value}
                              value={industry.value}
                              className="text-black"
                            >
                              {industry.label}
                            </option>
                          ))}
                        </Field>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg
                            className="fill-current h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                      <ErrorMessage
                        name="industry"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        Company
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <Field
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Company Name"
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 text-sm ${
                          touched.company && errors.company
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-200 focus:ring-primary"
                        }`}
                      />
                      <ErrorMessage
                        name="company"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="companySize"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        Company Size
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <div className="relative">
                        <Field
                          as="select"
                          id="companySize"
                          name="companySize"
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md appearance-none focus:outline-none focus:ring-1 text-[#9CA3AF] bg-white text-sm ${
                            touched.companySize && errors.companySize
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-200 focus:ring-primary"
                          }`}
                        >
                          <option value="" disabled>
                            Select Company Size
                          </option>
                          {companySizes.map((size) => (
                            <option
                              key={size.value}
                              value={size.value}
                              className="text-black"
                            >
                              {size.label}
                            </option>
                          ))}
                        </Field>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg
                            className="fill-current h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                      <ErrorMessage
                        name="companySize"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="country"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        Country
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <div className="relative">
                        <Field
                          as="select"
                          id="country"
                          name="country"
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md appearance-none focus:outline-none focus:ring-1 text-[#9CA3AF] bg-white text-sm ${
                            touched.country && errors.country
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-200 focus:ring-primary"
                          }`}
                        >
                          <option value="" disabled>
                            Select Country
                          </option>
                          {countries.map((country) => (
                            <option
                              key={country.value}
                              value={country.value}
                              className="text-black"
                            >
                              {country.label}
                            </option>
                          ))}
                        </Field>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg
                            className="fill-current h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                      <ErrorMessage
                        name="country"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm text-darkGray font-medium mb-1"
                      >
                        Select a Subject
                        <span className="text-red-500 ml-1 text-lg">*</span>
                      </label>
                      <div className="relative">
                        <Field
                          as="select"
                          id="subject"
                          name="subject"
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md appearance-none focus:outline-none focus:ring-1 text-[#9CA3AF] bg-white text-sm ${
                            touched.subject && errors.subject
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-200 focus:ring-primary"
                          }`}
                        >
                          <option value="" disabled>
                            Select a Subject
                          </option>
                          <option className="text-black" value="general">
                            General Inquiry
                          </option>
                          <option className="text-black" value="support">
                            Support
                          </option>
                          <option className="text-black" value="sales">
                            Sales
                          </option>
                          <option className="text-black" value="partnerships">
                            Partnerships
                          </option>
                        </Field>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg
                            className="fill-current h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                      <ErrorMessage
                        name="subject"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="body"
                        className="block text-sm font-medium text-darkGray mb-1"
                      >
                        How can we help you?
                      </label>
                      <Field
                        as="textarea"
                        id="body"
                        name="body"
                        placeholder="Please let us know what you're looking to solve with RegTechFlow"
                        rows={4}
                        maxLength={240}
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 resize-none text-sm ${
                          touched.body && errors.body
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-200 focus:ring-primary"
                        }`}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {values.body ? values.body.length : 0}/240
                        </span>
                      </div>
                      <ErrorMessage
                        name="body"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                      <span className="text-sm text-[#6B7280] mt-4">
                        My personal data will be processed in accordance with{" "}
                        <Link
                          to="/privacy-policy"
                          className="text-darkPurple font-medium"
                        >
                          RegTechFlow's Privacy Policy.
                        </Link>
                      </span>
                    </div>

                    <div className="pt-2 sm:pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-[230px] px-6  sm:px-8 py-2.5 sm:py-3 bg-white border border-primary text-primary rounded-full hover:bg-primary hover:text-white transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? "Sending..." : "Next"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookDemo;
